import React, { useEffect, useRef } from 'react'
import './ImageCarousel.css'
import IconButton from '@mui/material/IconButton'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

interface ImageCarouselProps {
  images: string[]
  selectedIndex: number
  onSelect: (index: number) => void
  showNavigationArrows?: boolean
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  selectedIndex,
  onSelect,
  showNavigationArrows = false,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollToCenter(selectedIndex)
  }, [selectedIndex])

  const scrollToCenter = (index: number) => {
    if (carouselRef.current) {
      const carousel = carouselRef.current
      const selectedImage = carousel.children[index] as HTMLElement
      const carouselRect = carousel.getBoundingClientRect()
      const imageRect = selectedImage.getBoundingClientRect()
      const scrollLeft = selectedImage.offsetLeft - (carouselRect.width / 2 - imageRect.width / 2)
      carousel.scrollTo({ left: scrollLeft, behavior: 'smooth' })
    }
  }

  const handleSelect = (index: number) => {
    onSelect(index)
    scrollToCenter(index)
  }

  return (
    <div className='carousel-container'>
      {showNavigationArrows && (
        <IconButton
          className='scroll-button'
          onClick={() => handleSelect(Math.max(selectedIndex - 1, 0))}
          disabled={selectedIndex === 0}
        >
          <ArrowBackIosIcon />
        </IconButton>
      )}
      <div className='carousel' ref={carouselRef}>
        {images.map((src, index) => (
          <img
            key={index}
            src={src}
            alt={`Image ${index}`}
            className={`carousel-image ${index === selectedIndex ? 'selected' : ''}`}
            onClick={(event) => {
              event.stopPropagation()
              handleSelect(index)
            }}
          />
        ))}
      </div>
      {showNavigationArrows && (
        <IconButton
          className='scroll-button'
          onClick={() => handleSelect(Math.min(selectedIndex + 1, images.length - 1))}
          disabled={selectedIndex === images.length - 1}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </div>
  )
}

export default ImageCarousel
